import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { isLocalPreviewMode } from '../lib/localPreview';
import useSEO from '../hooks/useSEO';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

let stripeScriptPromise = null;
const ensureStripeJs = () => {
  if (window.Stripe) {
    return Promise.resolve();
  }

  if (stripeScriptPromise) {
    return stripeScriptPromise;
  }

  stripeScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[src="https://js.stripe.com/v3"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return stripeScriptPromise;
};

const UpdatePaymentMethod = () => {
  useSEO({
    title: 'Update payment method | APDoneRight',
    description: 'Update the payment method used for your APDoneRight subscription.',
    canonical: '/manage-subscription/update-card',
    robots: 'noindex,follow',
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const localPreviewMode = isLocalPreviewMode();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [fallbackUrl, setFallbackUrl] = useState(location.state?.setupUrl || '');

  const stripeRef = useRef(null);
  const elementsRef = useRef(null);
  const paymentElementRef = useRef(null);
  const paymentElementInstanceRef = useRef(null);

  const prefetchedClientSecret = location.state?.clientSecret || null;

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      if (!user) {
        navigate('/signin');
        return;
      }

      if (localPreviewMode) {
        setFallbackUrl('');
        setLoading(false);
        return;
      }

      if (!profile?.stripe_customer_id) {
        setError('No subscription found. Start a plan before updating your payment method.');
        setLoading(false);
        return;
      }

      if (!STRIPE_PUBLISHABLE_KEY) {
        console.error('VITE_STRIPE_PUBLISHABLE_KEY is missing.');
        setError('Stripe publishable key is not configured. Please contact support.');
        setLoading(false);
        return;
      }

      try {
        await ensureStripeJs();
        if (!isMounted) return;

        const stripe = window.Stripe(STRIPE_PUBLISHABLE_KEY);
        if (!stripe) {
          throw new Error('Failed to initialize Stripe.js');
        }
        stripeRef.current = stripe;

        let clientSecret = prefetchedClientSecret;
        let setupIntentId = location.state?.setupIntentId || null;

        if (!clientSecret) {
          const { data, error: fnError } = await supabase.functions.invoke('create-setup-intent', {
            body: {}
          });

          if (!isMounted) return;

          if (fnError) {
            throw new Error(fnError.message);
          }
          if (data?.error) {
            throw new Error(data.error);
          }
          if (!data?.clientSecret) {
            throw new Error('Setup Intent did not include a client secret.');
          }

          setFallbackUrl(data.setupUrl || '');
          clientSecret = data.clientSecret;
          setupIntentId = data.setupIntentId ?? null;
        }

        const elements = stripe.elements({
          clientSecret,
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#111827',
              colorText: '#111827',
              colorBackground: '#ffffff',
              colorDanger: '#dc2626',
              borderRadius: '14px'
            }
          }
        });

        elementsRef.current = elements;
        paymentElementInstanceRef.current = elements.create('payment', {
          defaultValues: {
            billingDetails: {
              email: user.email || undefined
            }
          }
        });

        paymentElementInstanceRef.current.mount(paymentElementRef.current);
        setLoading(false);
      } catch (err) {
        console.error('Failed to initialize payment form:', err);
        setError(err.message || 'Unable to load payment form.');
        setLoading(false);
      }
    };

    init();

    return () => {
      isMounted = false;
      if (paymentElementInstanceRef.current) {
        paymentElementInstanceRef.current.destroy();
        paymentElementInstanceRef.current = null;
      }
      if (elementsRef.current) {
        elementsRef.current.destroy();
        elementsRef.current = null;
      }
    };
  }, [localPreviewMode, user, profile, navigate, prefetchedClientSecret]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (localPreviewMode) {
      setStatus('success');
      setMessage('Payment method update simulated in test mode.');
      setTimeout(() => {
        navigate('/manage-subscription?payment_updated=1');
      }, 1200);
      return;
    }
    if (!stripeRef.current || !elementsRef.current) {
      return;
    }

    setStatus('processing');
    setMessage('');

    const { error: confirmError } = await stripeRef.current.confirmSetup({
      elements: elementsRef.current,
      redirect: 'if_required',
      confirmParams: {
        return_url: `${window.location.origin}/manage-subscription?setup_success=true`
      }
    });

    if (confirmError) {
      console.error('Error confirming setup intent:', confirmError);
      setStatus('error');
      setMessage(confirmError.message || 'Payment method update failed. Please try again.');
      return;
    }

    setStatus('success');
    setMessage('Payment method updated successfully.');
    setTimeout(() => {
      navigate('/manage-subscription?payment_updated=1');
    }, 1500);
  };

  if (loading) {
    return (
      <>
        <div className="ap-payment-page">
        <div className="min-h-[70vh] flex items-center justify-center px-4">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-900 border-t-transparent"></div>
        </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="ap-payment-page">
        <div className="min-h-[70vh] flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white/80 backdrop-blur-xl border border-white/60 rounded-3xl p-8 shadow-xl">
            <h1 className="text-2xl font-bold text-gray-900 mb-3 font-serif">
              Update payment method
            </h1>
            <p className="text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3 mb-5 text-sm font-medium">
              {error}
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate('/manage-subscription')}
                className="w-full px-5 py-3 rounded-full bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors"
              >
                Back to billing
              </button>
              {fallbackUrl ? (
                <button
                  onClick={() => window.location.assign(fallbackUrl)}
                  className="w-full px-5 py-3 rounded-full border border-gray-200 bg-white text-gray-900 font-semibold hover:bg-gray-50 transition-colors"
                >
                  Open Stripe secure page
                </button>
              ) : null}
            </div>
          </div>
        </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="ap-payment-page relative min-h-screen overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-15%] left-[-10%] w-[55%] h-[55%] bg-teal-100/60 rounded-full blur-[110px] animate-pulse-slow" />
          <div
            className="absolute bottom-[-15%] right-[-10%] w-[55%] h-[55%] bg-purple-100/60 rounded-full blur-[110px] animate-pulse-slow"
            style={{ animationDelay: '1s' }}
          />
        </div>

        <div className="relative container mx-auto px-4 py-12 max-w-4xl">
          <div className="bg-white/80 backdrop-blur-xl border border-white/60 rounded-3xl p-8 shadow-xl">
            <h1 className="text-3xl font-bold text-gray-900 mb-3 font-serif">
              Update payment method
            </h1>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Securely update your saved card. Your new payment method will be used for the next billing cycle.
            </p>

            {localPreviewMode ? (
              <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">
                Test mode skips Stripe and lets you simulate a successful payment-method update.
              </div>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-6">
              {localPreviewMode ? (
                <div className="min-h-[200px] rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-600">
                  Stripe Elements are disabled in test mode.
                </div>
              ) : (
                <div
                  ref={paymentElementRef}
                  className="min-h-[200px] rounded-2xl border border-gray-200 bg-white p-5 shadow-inner"
                />
              )}

              {message ? (
                <div
                  className={`rounded-2xl border px-4 py-3 text-sm font-medium ${status === 'success'
                    ? 'border-teal-200 bg-teal-50 text-teal-900'
                    : 'border-red-200 bg-red-50 text-red-900'
                    }`}
                >
                  {message}
                </div>
              ) : null}

              <div className="flex flex-wrap gap-3">
                <button
                  type="submit"
                  disabled={status === 'processing'}
                  className="px-6 py-3 rounded-full bg-gray-900 hover:bg-gray-800 text-white font-semibold transition-colors disabled:opacity-60"
                >
                  {status === 'processing' ? 'Updating...' : 'Save payment method'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/manage-subscription')}
                  className="px-6 py-3 rounded-full border border-gray-200 bg-white text-gray-900 font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                {fallbackUrl ? (
                  <button
                    type="button"
                    onClick={() => window.location.assign(fallbackUrl)}
                    className="px-6 py-3 rounded-full border border-gray-200 bg-white text-gray-900 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Open Stripe secure page
                  </button>
                ) : null}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpdatePaymentMethod;
